import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const GoalModal = ({ isOpen, onClose, onSave, goal, isViewOnly, language = 'pt', initialStep = 1, loading = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    titulo: goal?.titulo || '',
    areaVida: goal?.areaVida || '',
    estadoAtual: goal?.estadoAtual || '',
    metaFutura: goal?.metaFutura || '',
    dataAlvo: goal?.dataAlvo || (() => {
      const date = new Date();
      date?.setMonth(date?.getMonth() + 3);
      return date?.toISOString()?.split('T')?.[0];
    })(),
    dataInicio: goal?.dataInicio || new Date()?.toISOString()?.split('T')?.[0],
    orcamento: goal?.orcamento || '',
    responsavel: goal?.responsavel || '',
    quemPodeAjudar: goal?.quemPodeAjudar || '',
    comoSeraFeito: goal?.comoSeraFeito || '',
    medidas: goal?.medidas || '',
    observacoes: goal?.observacoes || '',
    tarefasRelacionadas: goal?.tarefasRelacionadas || []
  });

  const [selectedTasks, setSelectedTasks] = useState(goal?.tarefasRelacionadas || []);

  const totalSteps = 11;

  // Reset form when goal changes and set initial step
  useEffect(() => {
    if (goal) {
      setFormData({
        titulo: goal?.titulo || '',
        areaVida: goal?.areaVida || '',
        estadoAtual: goal?.estadoAtual || '',
        metaFutura: goal?.metaFutura || '',
        dataAlvo: goal?.dataAlvo || (() => {
          const date = new Date();
          date?.setMonth(date?.getMonth() + 3);
          return date?.toISOString()?.split('T')?.[0];
        })(),
        dataInicio: goal?.dataInicio || new Date()?.toISOString()?.split('T')?.[0],
        orcamento: goal?.orcamento || '',
        responsavel: goal?.responsavel || '',
        quemPodeAjudar: goal?.quemPodeAjudar || '',
        comoSeraFeito: goal?.comoSeraFeito || '',
        medidas: goal?.medidas || '',
        observacoes: goal?.observacoes || '',
        tarefasRelacionadas: goal?.tarefasRelacionadas || []
      });
      setSelectedTasks(goal?.tarefasRelacionadas || []);
    }
    
    // Set initial step when modal opens
    if (isOpen) {
      setCurrentStep(initialStep);
    }
  }, [goal, isOpen, initialStep]);

  // Format currency value with proper mask
  const formatCurrency = (value) => {
    if (!value) return '';
    const numericValue = parseFloat(value) || 0;
    return new Intl.NumberFormat(language === 'pt' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: language === 'pt' ? 'BRL' : 'USD',
      minimumFractionDigits: 2,
    })?.format(numericValue);
  };

  const getLabels = () => {
    const labels = {
      pt: {
        viewGoal: 'Visualizar Meta',
        editGoal: 'Editar Meta',
        newGoal: 'Nova Meta',
        close: 'Fechar',
        cancel: 'Cancelar',
        saveGoal: 'Salvar Meta',
        saveChanges: 'Salvar Alterações',
        save: 'Salvar',
        previous: 'Anterior',
        next: 'Próximo',
        step: 'Passo',
        of: 'de',
        // Step titles
        goalTitle: 'Título da Meta',
        responsible: 'Responsável',
        lifeArea: 'Área da Vida',
        currentState: 'Estado Atual',
        futureGoal: 'Alcance (Futuro)',
        targetDate: 'Data Alvo',
        startDate: 'Data de Início',
        budget: 'Orçamento',
        whoCanHelp: 'Quem Pode Ajudar',
        howToDo: 'Como Será Feito',
        measures: 'Medidas',
        relatedTasks: 'Tarefas Relacionadas',
        // Placeholders
        goalTitlePlaceholder: 'Ex: Comprar um apartamento próprio',
        chooseArea: 'Escolha uma área',
        currentStatePlaceholder: 'Ex: Atualmente moro de aluguel e não tenho reserva de emergência...',
        futureGoalPlaceholder: 'Ex: Ter meu próprio apartamento de 2 quartos em uma região segura...',
        budgetPlaceholder: '0,00',
        responsiblePlaceholder: 'Ex: João Silva, Maria Santos, Mentor Financeiro...',
        whoCanHelpPlaceholder: 'Ex: Mentor financeiro, família, amigos...',
        howToDoPlaceholder: 'Descreva seu plano de ação detalhado...',
        measuresPlaceholder: 'Ex: Economizar 30% do salário, buscar financiamento...',
        // Questions
        goalTitleQuestion: 'Qual é o título da sua meta?',
        responsibleQuestion: 'Quem é responsável por esta meta?',
        lifeAreaQuestion: 'Selecione a área da vida',
        currentStateQuestion: 'Descreva sua situação atual',
        futureGoalQuestion: 'Descreva seu Estado Futuro',
        targetDateQuestion: 'Data alvo para atingir a meta',
        startDateQuestion: 'Data de início',
        budgetQuestion: 'Orçamento necessário (R$)',
        whoCanHelpQuestion: 'Quem pode te ajudar?',
        howToDoQuestion: 'Como será feito?',
        measuresQuestion: 'Quais medidas você vai tomar?',
        relatedTasksQuestion: 'Selecione as tarefas relacionadas'
      },
      en: {
        viewGoal: 'View Goal',
        editGoal: 'Edit Goal',
        newGoal: 'New Goal',
        close: 'Close',
        cancel: 'Cancel',
        saveGoal: 'Save Goal',
        saveChanges: 'Save Changes',
        save: 'Save',
        previous: 'Previous',
        next: 'Next',
        step: 'Step',
        of: 'of',
        // Step titles
        goalTitle: 'Goal Title',
        responsible: 'Responsible',
        lifeArea: 'Life Area',
        currentState: 'Current State',
        futureGoal: 'Future Goal',
        targetDate: 'Target Date',
        startDate: 'Start Date',
        budget: 'Budget',
        whoCanHelp: 'Who Can Help',
        howToDo: 'How to Do',
        measures: 'Measures',
        relatedTasks: 'Related Tasks',
        // Placeholders
        goalTitlePlaceholder: 'Ex: Buy my own apartment',
        chooseArea: 'Choose an area',
        currentStatePlaceholder: 'Ex: Currently I rent and have no emergency savings...',
        futureGoalPlaceholder: 'Ex: Have my own 2-bedroom apartment in a safe area...',
        budgetPlaceholder: '0.00',
        responsiblePlaceholder: 'Ex: John Silva, Mary Santos, Financial Mentor...',
        whoCanHelpPlaceholder: 'Ex: Financial mentor, family, friends...',
        howToDoPlaceholder: 'Describe your detailed action plan...',
        measuresPlaceholder: 'Ex: Save 30% of salary, seek financing...',
        // Questions
        goalTitleQuestion: 'What is the title of your goal?',
        responsibleQuestion: 'Who is responsible for this goal?',
        lifeAreaQuestion: 'Select the life area',
        currentStateQuestion: 'Describe your current situation',
        futureGoalQuestion: 'Describe your Future State',
        targetDateQuestion: 'Target date to achieve the goal',
        startDateQuestion: 'Start date',
        budgetQuestion: 'Required budget ($)',
        whoCanHelpQuestion: 'Who can help you?',
        howToDoQuestion: 'How will it be done?',
        measuresQuestion: 'What measures will you take?',
        relatedTasksQuestion: 'Select related tasks'
      }
    };
    return labels?.[language] || labels?.pt;
  };

  const labels = getLabels();
  
  const areaOptions = [
    { value: 'saude', label: language === 'en' ? 'Health' : 'Saúde' },
    { value: 'financas', label: language === 'en' ? 'Finance' : 'Finanças' },
    { value: 'carreira', label: language === 'en' ? 'Career' : 'Carreira' },
    { value: 'relacionamentos', label: language === 'en' ? 'Relationships' : 'Relacionamentos' },
    { value: 'pessoal', label: language === 'en' ? 'Personal Development' : 'Desenvolvimento Pessoal' },
    { value: 'familia', label: language === 'en' ? 'Family' : 'Família' },
    { value: 'lazer', label: language === 'en' ? 'Leisure' : 'Lazer' },
    { value: 'espiritualidade', label: language === 'en' ? 'Spirituality' : 'Espiritualidade' }
  ];

  const availableTasks = [
    { id: '1', label: language === 'en' ? 'Research market options' : 'Pesquisar opções no mercado' },
    { id: '2', label: language === 'en' ? 'Define budget' : 'Definir orçamento' },
    { id: '3', label: language === 'en' ? 'Create action timeline' : 'Criar cronograma de ações' },
    { id: '4', label: language === 'en' ? 'Seek specialized mentoring' : 'Buscar mentoria especializada' },
    { id: '5', label: language === 'en' ? 'Establish success metrics' : 'Estabelecer métricas de sucesso' },
    { id: '6', label: language === 'en' ? 'Review progress weekly' : 'Revisar progresso semanalmente' },
    { id: '7', label: language === 'en' ? 'Document lessons learned' : 'Documentar lições aprendidas' },
    { id: '8', label: language === 'en' ? 'Celebrate important milestones' : 'Celebrar marcos importantes' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTaskToggle = (taskId) => {
    const updatedTasks = selectedTasks?.includes(taskId)
      ? selectedTasks?.filter(id => id !== taskId)
      : [...selectedTasks, taskId];
    setSelectedTasks(updatedTasks);
    setFormData(prev => ({ ...prev, tarefasRelacionadas: updatedTasks }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData?.titulo?.trim()) {
      alert(language === 'en' ? 'Goal title is required' : 'O título da meta é obrigatório');
      return;
    }
    await onSave(formData);
    handleClose();
  };

  const handleSaveField = () => {
    // Salvar apenas o campo atual
    if (goal) {
      // Identificar qual campo está sendo editado baseado no currentStep
      const fieldMap = {
        1: 'titulo',           // Passo 1: Título da Meta
        2: 'responsavel',      // Passo 2: Responsável
        3: 'areaVida',         // Passo 3: Área da Vida
        4: 'estadoAtual',      // Passo 4: Estado Atual
        5: 'metaFutura',       // Passo 5: Alcance (Futuro)
        6: 'medidas',          // Passo 6: Medidas
        7: 'orcamento',        // Passo 7: Orçamento
        8: 'quemPodeAjudar',   // Passo 8: Quem Pode Ajudar
        9: 'comoSeraFeito',    // Passo 9: Como Será Feito
        10: 'dataInicio',      // Passo 10: Data de Início
        11: 'dataAlvo'         // Passo 11: Data Alvo
      };
      
      const currentField = fieldMap[currentStep];
      if (currentField && formData[currentField] !== undefined) {
        console.log('🔄 Salvando campo específico:', {
          currentField,
          currentStep,
          oldValue: goal[currentField],
          newValue: formData[currentField],
          goalId: goal.id
        });
        
        // Criar objeto com apenas o campo editado para evitar dados antigos
        const updateData = {
          [currentField]: formData[currentField]
        };
        
        console.log('📤 Dados sendo enviados para onSave:', updateData);
        
        // Passar apenas os dados necessários, não o objeto goal completo
        onSave(updateData);
      }
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      titulo: '',
      areaVida: '',
      estadoAtual: '',
      metaFutura: '',
      dataAlvo: (() => {
        const date = new Date();
        date?.setMonth(date?.getMonth() + 3);
        return date?.toISOString()?.split('T')?.[0];
      })(),
      dataInicio: new Date()?.toISOString()?.split('T')?.[0],
      orcamento: '',
      responsavel: '',
      comoSeraFeito: '',
      observacoes: '',
      tarefasRelacionadas: []
    });
    setSelectedTasks([]);
    onClose();
  };

  const getStepTitle = () => {
    const titles = {
      1: labels?.goalTitle,           // Passo 1: Título da Meta
      2: labels?.responsible,         // Passo 2: Responsável
      3: labels?.lifeArea,            // Passo 3: Área da Vida
      4: labels?.currentState,        // Passo 4: Estado Atual
      5: labels?.futureGoal,          // Passo 5: Alcance (Futuro)
      6: labels?.measures,            // Passo 6: Medidas
      7: labels?.budget,              // Passo 7: Orçamento
      8: labels?.whoCanHelp,          // Passo 8: Quem Pode Ajudar
      9: labels?.howToDo,             // Passo 9: Como Será Feito
      10: labels?.startDate,          // Passo 10: Data de Início
      11: labels?.targetDate          // Passo 11: Data Alvo
    };
    return titles?.[currentStep];
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Título da Meta
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-stretch w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.goalTitleQuestion}</label>
            <Input
              placeholder={labels?.goalTitlePlaceholder}
              value={formData?.titulo}
              onChange={(e) => handleInputChange('titulo', e?.target?.value)}
              disabled={isViewOnly}
              className="w-full p-3 border border-[#C89D4B] rounded-lg bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B]"
            />
          </div>
        );
      case 2: // Responsável
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-stretch w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.responsibleQuestion}</label>
            <Input
              placeholder={labels?.responsiblePlaceholder}
              value={formData?.responsavel}
              onChange={(e) => handleInputChange('responsavel', e?.target?.value)}
              disabled={isViewOnly}
              className="w-full p-3 border border-[#C89D4B] rounded-lg bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B]"
            />
          </div>
        );
      case 3: // Área da Vida
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.lifeAreaQuestion}</label>
            <Select
              options={areaOptions}
              value={formData?.areaVida}
              onChange={(value) => handleInputChange("areaVida", value)}
              placeholder={labels?.chooseArea}
              disabled={isViewOnly}
              className="w-full p-3 z-50 border border-[#C89D4B] rounded-lg bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B]"
              menuPortalTarget={document.body} // força renderizar no body
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999, // garante que o dropdown fica na frente de tudo
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: "#4B1E1E", // fundo vinho
                  border: "1px solid #C89D4B",
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused
                    ? "#C89D4B" // hover dourado
                    : "#4B1E1E", // fundo vinho normal
                  color: state.isSelected ? "#4B1E1E" : "#C89D4B", // selecionado inverte: texto vinho no dourado
                  cursor: "pointer",
                }),
                singleValue: (base) => ({
                  ...base,
                  color: "#C89D4B", // valor selecionado dourado
                }),
                placeholder: (base) => ({
                  ...base,
                  color: "#C89D4B", // placeholder dourado
                }),
                input: (base) => ({
                  ...base,
                  color: "#C89D4B", // texto digitado dourado (quando search estiver habilitado)
                }),
              }}
            />
          </div>
        );
      case 4: // Estado Atual
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.currentStateQuestion}</label>
            <textarea
              className="w-full p-3 border border-[#C89D4B] rounded-lg resize-none h-32 bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B] disabled:opacity-50"
              placeholder={labels?.currentStatePlaceholder}
              value={formData?.estadoAtual}
              onChange={(e) => handleInputChange('estadoAtual', e?.target?.value)}
              disabled={isViewOnly}
            />
          </div>
        );
      case 5: // Alcance (Futuro)
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.futureGoalQuestion}</label>
            <textarea
              className="w-full p-3 border border-[#C89D4B] rounded-lg resize-none h-32 bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B] disabled:opacity-50"
              placeholder={labels?.futureGoalPlaceholder}
              value={formData?.metaFutura}
              onChange={(e) => handleInputChange('metaFutura', e?.target?.value)}
              disabled={isViewOnly}
            />
          </div>
        );
      case 6: // Medidas
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.measuresQuestion}</label>
            <textarea
              className="w-full p-3 border border-[#C89D4B] rounded-lg resize-none h-32 bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B] disabled:opacity-50"
              placeholder={labels?.measuresPlaceholder}
              value={formData?.medidas}
              onChange={(e) => handleInputChange('medidas', e?.target?.value)}
              disabled={isViewOnly}
            />
          </div>
        );
      case 7: // Orçamento
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.budgetQuestion}</label>
            <Input
              type="number"
              placeholder={labels?.budgetPlaceholder}
              value={formData?.orcamento}
              onChange={(e) => handleInputChange('orcamento', e?.target?.value)}
              disabled={isViewOnly}
              className="w-full p-3 border border-[#C89D4B] rounded-lg bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B]"
            />
          </div>
        );
      case 8: // Quem Pode Ajudar
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-stretch w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.whoCanHelpQuestion}</label>
            <Input
              placeholder={labels?.whoCanHelpPlaceholder}
              value={formData?.quemPodeAjudar}
              onChange={(e) => handleInputChange('quemPodeAjudar', e?.target?.value)}
              disabled={isViewOnly}
              className="w-full p-3 border border-[#C89D4B] rounded-lg bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B]"
            />
          </div>
        );
      case 9: // Como Será Feito
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.howToDoQuestion}</label>
            <textarea
              className="w-full p-3 border border-[#C89D4B] rounded-lg resize-none h-32 bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B] disabled:opacity-50"
              placeholder={labels?.howToDoPlaceholder}
              value={formData?.comoSeraFeito}
              onChange={(e) => handleInputChange('comoSeraFeito', e?.target?.value)}
              disabled={isViewOnly}
          />
          </div>
        );
      case 10: // Data de Início
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.startDateQuestion}</label>
            <Input
              type="date"
              value={formData?.dataInicio}
              onChange={(e) => handleInputChange('dataInicio', e?.target?.value)}
              disabled={isViewOnly}
              className="w-full p-3 border border-[#C89D4B] rounded-lg bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B]"
            />
          </div>
        );
      case 11: // Data Alvo
        return (
          <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-4">
            <label className="text-md font-medium text-foreground mb-2 text-center">{labels?.targetDateQuestion}</label>
            <Input
              type="date"
              value={formData?.dataAlvo}
              onChange={(e) => handleInputChange('dataAlvo', e?.target?.value)}
              disabled={isViewOnly}
              className="w-full p-3 border border-[#C89D4B] rounded-lg bg-[#4B1E1E] text-[#C89D4B] focus:outline-none focus:ring-2 focus:ring-[#C89D4B]/20 focus:border-[#C89D4B]"
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-3">
            <h2 className="text-lg sm:text-xl font-heading font-semibold text-foreground">
              {isViewOnly ? labels?.viewGoal : goal ? labels?.editGoal : labels?.newGoal}
            </h2>
            {!isViewOnly && (
              <span className="text-xs sm:text-sm text-muted-foreground bg-muted px-2 py-1 rounded-full">
                {currentStep}/{totalSteps}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleClose}
            className="h-8 w-8 sm:h-10 sm:w-10"
          />
        </div>

        {/* Progress Bar - only show in multi-step mode */}
        {!isViewOnly && (
          <div className="px-4 sm:px-6 pt-2 sm:pt-4 flex-shrink-0">
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6">
            {isViewOnly ? (
              // Enhanced View Mode with better styling
              (<div className="space-y-6">
                {/* Main Information Section */}
                <div className="bg-muted/20 rounded-lg p-4 border border-border/50">
                  <h3 className="text-base font-bold text-foreground mb-3 border-b border-border pb-2">
                    📋 {language === 'pt' ? 'Informações Principais' : 'Main Information'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-primary block mb-1">
                        {labels?.goalTitle}
                      </label>
                      <p className="text-foreground bg-background/50 p-2 rounded border">{goal?.titulo}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-semibold text-primary block mb-1">
                        {labels?.lifeArea}
                      </label>
                      <p className="text-foreground bg-background/50 p-2 rounded border">
                        {areaOptions?.find(opt => opt?.value === goal?.areaVida)?.label || goal?.areaVida}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Situation Section */}
                {(goal?.estadoAtual || goal?.metaFutura) && (
                  <div className="bg-muted/20 rounded-lg p-4 border border-border/50">
                    <h3 className="text-base font-bold text-foreground mb-3 border-b border-border pb-2">
                      🎯 {language === 'pt' ? 'Situação' : 'Situation'}
                    </h3>
                    
                    {goal?.estadoAtual && (
                      <div className="mb-4">
                        <label className="text-sm font-semibold text-primary block mb-1">
                          {labels?.currentState}
                        </label>
                        <p className="text-foreground bg-background/50 p-2 rounded border">{goal?.estadoAtual}</p>
                      </div>
                    )}
                    
                    {goal?.metaFutura && (
                      <div>
                        <label className="text-sm font-semibold text-primary block mb-1">
                          {labels?.futureGoal}
                        </label>
                        <p className="text-foreground bg-background/50 p-2 rounded border">{goal?.metaFutura}</p>
                      </div>
                    )}
                  </div>
                )}
                {/* Timeline & Budget Section */}
                <div className="bg-muted/20 rounded-lg p-4 border border-border/50">
                  <h3 className="text-base font-bold text-foreground mb-3 border-b border-border pb-2">
                    📅 {language === 'pt' ? 'Cronograma & Orçamento' : 'Timeline & Budget'}
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-primary block mb-1">
                        {labels?.startDate}
                      </label>
                      <p className="text-foreground bg-background/50 p-2 rounded border">
                        {goal?.dataInicio ? new Date(goal?.dataInicio + 'T00:00:00')?.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-US') : '-'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-primary block mb-1">
                        {labels?.targetDate}
                      </label>
                      <p className="text-foreground bg-background/50 p-2 rounded border">
                        {goal?.dataAlvo ? new Date(goal?.dataAlvo + 'T00:00:00')?.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-US') : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {goal?.orcamento && (
                    <div className="mt-4">
                      <label className="text-sm font-semibold text-primary block mb-1">
                        {labels?.budget}
                      </label>
                      <p className="text-foreground bg-background/50 p-2 rounded border text-lg font-medium">
                        {formatCurrency(goal?.orcamento)}
                      </p>
                    </div>
                  )}
                </div>
                {/* Team & Execution Section */}
                {(goal?.responsavel || goal?.comoSeraFeito) && (
                  <div className="bg-muted/20 rounded-lg p-4 border border-border/50">
                    <h3 className="text-base font-bold text-foreground mb-3 border-b border-border pb-2">
                      👥 {language === 'pt' ? 'Equipe & Execução' : 'Team & Execution'}
                    </h3>
                    
                    {goal?.responsavel && (
                      <div className="mb-4">
                        <label className="text-sm font-semibold text-primary block mb-1">
                          {labels?.whoCanHelp}
                        </label>
                        <p className="text-foreground bg-background/50 p-2 rounded border">{goal?.responsavel}</p>
                      </div>
                    )}
                    
                    {goal?.comoSeraFeito && (
                      <div>
                        <label className="text-sm font-semibold text-primary block mb-1">
                          {labels?.howToDo}
                        </label>
                        <p className="text-foreground bg-background/50 p-2 rounded border">{goal?.comoSeraFeito}</p>
                      </div>
                    )}
                  </div>
                )}
                {/* Tasks Section */}
                {goal?.tarefasRelacionadas?.length > 0 && (
                  <div className="bg-muted/20 rounded-lg p-4 border border-border/50">
                    <h3 className="text-base font-bold text-foreground mb-3 border-b border-border pb-2">
                      ✅ {labels?.relatedTasks}
                    </h3>
                    <div className="space-y-2">
                      {goal?.tarefasRelacionadas?.map(taskId => {
                        const task = availableTasks?.find(t => t?.id === taskId);
                        return task ? (
                          <div key={taskId} className="flex items-center text-foreground bg-background/50 p-2 rounded border">
                            <div className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></div>
                            <span className="text-sm">{task?.label}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>)
            ) : (
              // Multi-step mode
              (<div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-foreground mb-2">{getStepTitle()}</h3>
                  <p className="text-sm text-muted-foreground">
                    {labels?.step} {currentStep} {labels?.of} {totalSteps}
                  </p>
                </div>
                <div className="min-h-[200px] flex flex-col justify-start items-center w-full max-w-7xl mx-auto mt-8">
                  {renderStepContent()}
                </div>
              </div>)
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-t border-border bg-muted/20 flex-shrink-0">
          {isViewOnly ? (
            <div className="flex space-x-2 sm:space-x-3 ml-auto">
              <Button variant="outline" onClick={handleClose} className="text-sm sm:text-base px-3 sm:px-4">
                {labels?.close}
              </Button>
            </div>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                iconName="ChevronLeft"
                iconPosition="left"
                className="text-sm sm:text-base px-3 sm:px-4"
              >
                {labels?.previous}
              </Button>
              
              <div className="flex space-x-2 sm:space-x-3">
                <Button variant="outline" onClick={handleClose} className="text-sm sm:text-base px-3 sm:px-4">
                  {labels?.cancel}
                </Button>
                
                {/* Botão Salvar entre Cancelar e Próximo/Concluir - Oculto no último passo */}
                {currentStep !== totalSteps && (
                  <Button
                    onClick={handleSaveField}
                    iconName="Save"
                    iconPosition="left"
                    className="bg-green-600 hover:bg-green-700 text-white text-sm sm:text-base px-3 sm:px-4"
                  >
                    {labels?.save}
                  </Button>
                )}
                
                {currentStep === totalSteps ? (
                  <Button
                    onClick={handleSubmit}
                    iconName="Check"
                    iconPosition="left"
                    disabled={loading}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm sm:text-base px-3 sm:px-4"
                  >
                    {loading ? (language === 'en' ? 'Saving...' : 'Salvando...') : (goal ? labels?.saveChanges : labels?.saveGoal)}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    iconName="ChevronRight"
                    iconPosition="right"
                    className="text-sm sm:text-base px-3 sm:px-4"
                  >
                    {labels?.next}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoalModal;